import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Button,
  FormError,
  FormGroup,
  FormLabel,
  Modal,
  Select,
  TextInput,
} from "@components";
import DateTimePicker from "@components/datePickers/DateTimePicker";
import { fetchCustomersV2, useUsers } from "@api";
import { useUser } from "@auth";
import { buildApiQuery } from "@utils";
import SearchSelect from "../../queries/components/SearchSelect";

const combine = (date, time) => {
  if (!date) return null;
  const [h = 0, m = 0] = (time || "00:00").split(":").map(Number);
  const [y, mo, d] = date.split("-").map(Number);
  return new Date(y, mo - 1, d, h, m, 0, 0);
};

// A "yyyy-MM-ddTHH:mm" field value -> a Date. All-day drops the time (midnight local).
const fromLocal = (value, allDay) => {
  if (!value) return null;
  const [date, time] = value.split("T");
  return combine(date, allDay ? null : time);
};

const searchCustomers = (term, opts) =>
  fetchCustomersV2(
    buildApiQuery({ page: 1, pageSize: 10, searchTerm: term, sort: "name", sortDirection: "asc" }),
    opts,
  ).then((r) => r?.data ?? []);

const toFormDefaults = (item, defaultDate) => {
  const start = item?.start ?? null;
  const end = item?.end ?? null;
  const due = item?.due ?? (!item ? defaultDate : null) ?? null;

  const fmt = (date) => (date ? format(date, "yyyy-MM-dd'T'HH:mm") : "");
  return {
    summary: item?.summary ?? "",
    kind: item?.kind ?? "task",
    status: item?.status ?? "",
    allDay: item?.allDay ?? false,
    start: fmt(start),
    end: fmt(end) || fmt(start),
    due: fmt(due),
    description: item?.description ?? "",
  };
};

/**
 * Create/edit modal for a calendar item. New items are always tasks, created over REST via `onCreate`
 * so they can carry an assignee and customer. Edits produce an object the CalDAV layer serialises to
 * ICS; existing uid/href/etag are preserved so they PUT back with optimistic concurrency.
 */
const EventForm = ({ isOpen, onClose, onSave, onCreate, saving, item, defaultDate, defaultCustomer = null }) => {
  const isEdit = !!item?.uid;
  const { user } = useUser();
  const { data: users } = useUsers();
  const [assignee, setAssignee] = useState(null);
  const [customer, setCustomer] = useState(null);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({ defaultValues: toFormDefaults(item, defaultDate) });

  // Reset only when the form OPENS or switches to a different item — keyed on the item's uid, not the
  // item object itself. The calendar refetches in the background (every 60s / on focus), handing this
  // a new `item` reference each time; resetting on that would silently wipe in-progress edits.
  useEffect(() => {
    if (!isOpen) return;
    reset(toFormDefaults(item, defaultDate));
    setAssignee(null);
    setCustomer(defaultCustomer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, item?.uid]);

  const kind = watch("kind");
  const allDay = watch("allDay");

  const statusOptions = useMemo(
    () =>
      kind === "task"
        ? [
            // No IN-PROCESS: the domain has no "started" state, so it would collapse to Confirmed
            // (NEEDS-ACTION) on save and silently revert. Offer only statuses that persist.
            ["NEEDS-ACTION", "Needs action"],
            ["COMPLETED", "Completed"],
            ["CANCELLED", "Cancelled"],
          ]
        : [
            ["CONFIRMED", "Confirmed"],
            ["TENTATIVE", "Tentative"],
            ["CANCELLED", "Cancelled"],
          ],
    [kind],
  );

  const submit = (values) => {
    if (!isEdit) {
      onCreate({
        title: values.summary.trim(),
        description: values.description.trim(),
        dueAt: fromLocal(values.due, false),
        assigneeUserId: assignee?.id ?? null,
        customerId: customer?.id ?? null,
      });
      return;
    }

    const isTask = values.kind === "task";
    const start = isTask ? null : fromLocal(values.start, values.allDay);
    let end = isTask ? null : fromLocal(values.end, values.allDay);
    if (!isTask && values.allDay) {
      // DTEND is exclusive for all-day events.
      end = new Date((end ?? start).getTime() + 24 * 60 * 60 * 1000);
    }

    onSave({
      // Spread the original item first so location/categories/rrule set server-side round-trip
      // untouched — the form doesn't expose them (the calendar service's write-back ignores those
      // properties, so editing them here would be a silent no-op), but we must not drop them either.
      ...item,
      uid: item?.uid ?? null,
      // Preserve the kind: a task saved as "event" serialises to a VEVENT, so the calendar service
      // reads its NEEDS-ACTION status as an event status (Tentative) — which drops it out of
      // Today's tasks (Confirmed only). Keep it a VTODO so the status round-trips to Confirmed.
      kind: isTask ? "task" : "event",
      summary: values.summary.trim(),
      description: values.description.trim(),
      allDay: values.allDay,
      start,
      end,
      due: isTask ? fromLocal(values.due, values.allDay) : null,
      status: values.status || (isTask ? "NEEDS-ACTION" : "CONFIRMED"),
      sequence: isEdit ? (item.sequence ?? 0) + 1 : 0,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? "Edit item" : "New task"}
      size="sm"
    >
      <form onSubmit={handleSubmit(submit)} className="flex h-full flex-col">
        <div className="flex-1 overflow-y-auto p-5">
          <FormGroup>
            <FormLabel>Title</FormLabel>
            <TextInput
              name="summary"
              control={control}
              displayName="Title"
              placeholder="Add a title"
              required
              errors={errors.summary}
            />
            <FormError errors={errors} name="summary" />
          </FormGroup>

          {isEdit ? (
            <FormGroup>
              <FormLabel>Status</FormLabel>
              <Select name="status" control={control} displayName="Status">
                {statusOptions.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </FormGroup>
          ) : (
            <>
              <FormGroup>
                <SearchSelect
                  label="Assign to"
                  placeholder={user?.name ? `${user.name} (you)` : "You"}
                  value={assignee}
                  onSelect={setAssignee}
                  displayValue={(u) => u.name || u.email}
                  getKey={(u) => u.id}
                  renderOption={(u) => (
                    <>
                      <span className="text-sm text-zinc-900">{u.name || u.email}</span>
                      {u.name && u.email && <span className="text-xs text-zinc-500">{u.email}</span>}
                    </>
                  )}
                  options={users ?? []}
                  filterOption={(u, term) =>
                    `${u.name ?? ""} ${u.email ?? ""}`.toLowerCase().includes(term.toLowerCase())
                  }
                  emptyText="No users found"
                />
              </FormGroup>
              <FormGroup>
                <SearchSelect
                  label="Customer"
                  placeholder="Optional — search customers…"
                  value={customer}
                  onSelect={setCustomer}
                  displayValue={(c) => (c.accountCode ? `${c.name} · ${c.accountCode}` : c.name)}
                  getKey={(c) => c.id}
                  renderOption={(c) => (
                    <>
                      <span className="text-sm text-zinc-900">{c.name}</span>
                      {c.accountCode && <span className="text-xs text-zinc-500">{c.accountCode}</span>}
                    </>
                  )}
                  search={searchCustomers}
                  queryKey={["calendar-task", "customers"]}
                  emptyText="No customers found"
                />
              </FormGroup>
            </>
          )}

          {kind === "event" ? (
            <>
              <FormGroup>
                <FormLabel>Starts</FormLabel>
                <Controller
                  name="start"
                  control={control}
                  rules={{ required: "A start is required" }}
                  render={({ field, fieldState }) => (
                    <DateTimePicker
                      value={field.value}
                      onChange={field.onChange}
                      dateOnly={allDay}
                      invalid={!!fieldState.error}
                      placeholder="Pick a start"
                    />
                  )}
                />
                <FormError errors={errors} name="start" />
              </FormGroup>
              <FormGroup>
                <FormLabel>Ends</FormLabel>
                <Controller
                  name="end"
                  control={control}
                  rules={{
                    validate: (value, values) => {
                      if (!value || !values.start) return true;
                      return (
                        fromLocal(value, values.allDay) >= fromLocal(values.start, values.allDay) ||
                        "End must be after start"
                      );
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <DateTimePicker
                      value={field.value}
                      onChange={field.onChange}
                      dateOnly={allDay}
                      invalid={!!fieldState.error}
                      placeholder="Pick an end"
                    />
                  )}
                />
                <FormError errors={errors} name="end" />
              </FormGroup>
            </>
          ) : (
            <FormGroup>
              <FormLabel>When</FormLabel>
              <Controller
                name="due"
                control={control}
                rules={{ required: "A date is required" }}
                render={({ field, fieldState }) => (
                  <DateTimePicker
                    value={field.value}
                    onChange={field.onChange}
                    dateOnly={allDay}
                    invalid={!!fieldState.error}
                    placeholder="Pick a date & time"
                  />
                )}
              />
              <FormError errors={errors} name="due" />
            </FormGroup>
          )}

          <FormGroup className="mb-2">
            <FormLabel>Description</FormLabel>
            <TextInput
              name="description"
              control={control}
              displayName="Description"
              placeholder="Add a description"
              area
              rows={3}
            />
          </FormGroup>
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-zinc-200 p-4">
          <Button color="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button color="primary" type="submit" submitting={saving}>
            {isEdit ? "Save changes" : "Create task"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default EventForm;
